import { useState } from 'react';
import { Link } from 'react-router';
import { formatDateTime } from '@brimo/shared';
import { Banner, Button, Pill, Textarea } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { useWorkspace } from '../../../lib/session';
import type { ApprovalDetail } from '../../approvals/api';
import { decideAssignment, useInvalidateFinancings } from '../api';
import styles from './approval.module.css';

export interface FinancierAssignmentPanelProps {
  approval: ApprovalDetail;
  /** Called after „Freigeben" / „Ablehnen" went through. */
  onDecided?: (() => void) | undefined;
}

const statusPill: Record<
  ApprovalDetail['status'],
  { label: string; variant: 'warning' | 'success' | 'danger' | 'neutral' }
> = {
  open: { label: 'offen', variant: 'warning' },
  approved: { label: 'freigegeben', variant: 'success' },
  rejected: { label: 'abgelehnt', variant: 'danger' },
  withdrawn: { label: 'zurückgezogen', variant: 'neutral' },
};

/**
 * „Finanzierer-Zuweisung" inside the approvals dialog (Backoffice 09 layout, D4): the financing, the proposed
 * financier and the proposer; „Ablehnen" (mandatory reason) / „Freigeben" → `POST /financings/:id/assignment/decide`.
 * The approvals module marks the type as decided elsewhere; the financings route answers who may decide.
 */
export function FinancierAssignmentPanel({ approval, onDecided }: FinancierAssignmentPanelProps) {
  const workspace = useWorkspace();
  const invalidate = useInvalidateFinancings();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<'approved' | 'rejected' | null>(null);
  const financingId = approval.refs.financingId;
  const financing = approval.entity.financing;
  const own = approval.requestedBy?.id === workspace.user.id && approval.recipient.id !== workspace.user.id;
  const open = approval.status === 'open';
  const pill = statusPill[approval.status];

  async function decide(decision: 'approved' | 'rejected') {
    if (financingId === null || pending) return;
    if (decision === 'rejected' && reason.trim() === '') {
      setReasonError('Bitte geben Sie einen Grund an.');
      return;
    }
    setPending(decision);
    setError(null);
    setReasonError(null);
    try {
      const body: { approvalId: string; decision: 'approved' | 'rejected'; reason?: string } = {
        approvalId: approval.id,
        decision,
      };
      if (decision === 'rejected') body.reason = reason.trim();
      await decideAssignment(financingId, body);
      await invalidate(financingId);
      setRejecting(false);
      onDecided?.();
    } catch (cause) {
      if (cause instanceof ApiError && cause.fields['reason']) setReasonError(cause.fields['reason']);
      else setError(errorMessage(cause));
    } finally {
      setPending(null);
    }
  }

  if (financingId === null) {
    return <Banner tone="danger">Diese Freigabe verweist auf keine Finanzierung.</Banner>;
  }

  return (
    <div className={styles.stack}>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {open && own ? (
        <Banner tone="info">{`Diese Freigabe entscheidet ${approval.recipient.name}.`}</Banner>
      ) : null}
      <div className={styles.head}>
        <Pill variant={pill.variant}>{pill.label}</Pill>
        <span className={styles.meta}>
          {approval.requestedBy ? `${approval.requestedBy.name} · ` : ''}
          {formatDateTime(approval.requestedAt)} · wartet seit {approval.waitingLabel}
        </span>
        {open && !own && !rejecting ? (
          <span className={styles.actions}>
            <Button variant="secondary" disabled={pending !== null} onClick={() => setRejecting(true)}>
              Ablehnen
            </Button>
            <Button loading={pending === 'approved'} onClick={() => void decide('approved')}>
              Freigeben
            </Button>
          </span>
        ) : null}
      </div>
      {!open ? (
        <div className={styles.meta}>
          {approval.decidedBy ? `${approval.decidedBy.name} · ` : ''}
          {approval.decidedAt ? formatDateTime(approval.decidedAt) : ''}
          {approval.reason ? ` · ${approval.reason}` : ''}
        </div>
      ) : null}
      <dl className={styles.rows}>
        {financing ? (
          <div className={styles.row}>
            <dt>Finanzierung</dt>
            <dd>
              <Link to={financing.href}>{financing.label}</Link>
            </dd>
          </div>
        ) : null}
        {approval.entity.deal ? (
          <div className={styles.row}>
            <dt>Deal</dt>
            <dd>
              <Link to={approval.entity.deal.href}>{approval.entity.deal.label}</Link>
            </dd>
          </div>
        ) : null}
        {approval.entity.proposedFinancier ? (
          <div className={styles.row}>
            <dt>Vorgeschlagen</dt>
            <dd>{approval.entity.proposedFinancier.name}</dd>
          </div>
        ) : null}
        <div className={styles.row}>
          <dt>Vorschlag von</dt>
          <dd>{approval.requestedBy?.name ?? 'System'}</dd>
        </div>
        {approval.message ? (
          <div className={styles.row}>
            <dt>Nachricht</dt>
            <dd>{approval.message}</dd>
          </div>
        ) : null}
      </dl>
      {rejecting ? (
        <div className={styles.reject}>
          <Textarea
            label="Grund der Ablehnung"
            required
            rows={3}
            autoFocus
            value={reason}
            error={reasonError ?? undefined}
            onChange={(event) => setReason(event.target.value)}
          />
          <span className={styles.actions}>
            <Button variant="secondary" disabled={pending !== null} onClick={() => setRejecting(false)}>
              Abbrechen
            </Button>
            <Button
              variant="destructiveFilled"
              loading={pending === 'rejected'}
              onClick={() => void decide('rejected')}
            >
              Ablehnung bestätigen
            </Button>
          </span>
        </div>
      ) : null}
    </div>
  );
}
